import { useId, useState, type ReactNode } from "react";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/design-system";
import type { EquipmentGroup } from "@/lib/api/oprema";
import { EquipmentBack } from "./EquipmentNavigation";

export const equipmentGroups: Record<EquipmentGroup, string> = {
  skela: "Skele",
  oplata: "Oplate",
  podupiraci: "Podupirači",
  alat: "Alati i strojevi",
  ostalo: "Ostalo",
};
export interface EquipmentListItem {
  id: string;
  name: string;
  group: EquipmentGroup;
  count?: number;
  subtitle?: string;
  badges?: ReactNode;
  countLabel?: string;
  piece?: boolean;
  typeId?: string;
}
function groupCount(items: EquipmentListItem[]) {
  const pieces = items.filter((item) => item.piece).length;
  const types = new Set(
    items.filter((item) => !item.piece).map((item) => item.typeId ?? item.id),
  ).size;
  const noun = (n: number, one: string, few: string, many: string) =>
    n % 100 >= 11 && n % 100 <= 14
      ? many
      : n % 10 === 1
        ? one
        : n % 10 >= 2 && n % 10 <= 4
          ? few
          : many;
  return [
    types ? `${types} ${noun(types, "vrsta", "vrste", "vrsta")}` : "",
    pieces ? `${pieces} ${noun(pieces, "komad", "komada", "komada")}` : "",
  ]
    .filter(Boolean)
    .join(" · ");
}
export function EquipmentList({
  items,
  onOpen,
  label,
  alwaysSearch = false,
}: {
  items: EquipmentListItem[];
  onOpen: (id: string) => void;
  label: string;
  alwaysSearch?: boolean;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("");
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const groups = Object.entries(equipmentGroups).filter(([key]) =>
    items.some((item) => item.group === key),
  );
  const filtered = items.filter(
    (item) =>
      (!group || item.group === group) &&
      `${item.name} ${item.subtitle ?? ""}`
        .toLocaleLowerCase("hr")
        .includes(search.toLocaleLowerCase("hr")),
  );
  return (
    <section className="equipment-list" aria-label={label}>
      {((alwaysSearch && items.length > 0) ||
        items.length > 10 ||
        groups.length > 1) && (
        <div className="equipment-list-filters">
          <label>
            <span>
              <Icon name="search" size={18} /> Pretraži
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {groups.length > 1 && (
            <div
              className="equipment-choices"
              role="group"
              aria-label="Skupina"
            >
              {[["", "Sve skupine"], ...groups].map(([value, name]) => (
                <Button
                  type="button"
                  variant="outline"
                  key={value}
                  aria-pressed={group === value}
                  onClick={() => setGroup(value!)}
                >
                  {name}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
      {Object.entries(equipmentGroups).map(([key, name]) => {
        const rows = filtered.filter((item) => item.group === key);
        if (!rows.length) return null;
        const open = !collapsed.includes(key);
        return (
          <div className="equipment-list-group" key={key}>
            <Button
              variant="outline"
              className="equipment-group-heading"
              aria-expanded={open}
              aria-controls={`${id}-${key}`}
              onClick={() =>
                setCollapsed((old) =>
                  open ? [...old, key] : old.filter((g) => g !== key),
                )
              }
            >
              <Icon name={open ? "chevronDown" : "chevronRight"} size={20} />
              {name}{" "}
              <span className="text-muted-foreground">{groupCount(rows)}</span>
            </Button>
            <div id={`${id}-${key}`} hidden={!open}>
              {rows.map((item) => (
                <Button
                  key={item.id}
                  variant="outline"
                  className="equipment-list-row"
                  aria-label={`Otvori: ${item.name}${item.subtitle ? ` · ${item.subtitle}` : ""}`}
                  onClick={() => onOpen(item.id)}
                >
                  <span className="equipment-list-name">
                    <b>{item.name}</b>
                    {item.subtitle && <small>{item.subtitle}</small>}
                    {item.badges && (
                      <span className="equipment-list-badges">
                        {item.badges}
                      </span>
                    )}
                  </span>
                  {item.countLabel ? (
                    <span className="equipment-list-arriving">
                      {item.countLabel}
                    </span>
                  ) : (
                    item.count !== undefined && (
                      <strong className="equipment-list-count">
                        {item.count}
                      </strong>
                    )
                  )}
                  <Icon name="chevronRight" size={20} />
                </Button>
              ))}
            </div>
          </div>
        );
      })}
      {!filtered.length && <p>Nema stavki za ovaj prikaz.</p>}
    </section>
  );
}

export function EquipmentDetail({
  title,
  children,
  onClose,
  crumbs,
  pending = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  crumbs?: { label: string; to?: string }[];
  pending?: boolean;
}) {
  return (
    <Dialog
      fullscreen
      title={title}
      onClose={onClose}
      className="equipment-detail"
    >
      <div className="site-equipment">
        <span className="equipment-grab" aria-hidden="true" />
        <EquipmentBack
          disabled={pending}
          crumbs={crumbs ?? [{ label: title }]}
        />
        {children}
      </div>
    </Dialog>
  );
}
